import { JSX, useEffect, useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import styled from "styled-components"

import {
  useLazyGetDeviceMessageHistoryQuery,
  useLazyGetDevicesMessagesQuery,
  useLazyGetMessageTemplatesQuery,
} from "store/api/contractorsApi"
import { useAppSelector } from "store/hooks"
import MessageDeliveryStatus from "types/enums/MessageDeliveryStatus"
import SubscriptionPrivilege from "types/enums/SubscriptionPrivilege"
import { ReactComponent as IconClock } from "assets/svg/clock.svg"
import { ReactComponent as IconCheck } from "assets/svg/icon-check.svg"
import { ReactComponent as IconCross } from "assets/svg/icon-table-cross.svg"

import { parseDateField } from "utils/dateUtils"
import hasSubscriptionAccess from "utils/hasSubscriptionAccess"
import { RestrictedAccessWidget } from "components/common/RestrictedAccessWidget"
import { Spinner } from "components/common/Spinner"

import { useCustomerDashboard } from "../../CustomerDashboardContext"
import {
  LoadingContainer,
  NoAlertsText,
  StyledWidgetButton,
  Widget,
  WidgetButton,
  WidgetContent,
  WidgetTitle,
} from "../styled"

const MAX_ITEMS = 2
const DATE_FORMAT = "MM.DD.YYYY | hh:mmA"

const STATUS_COLORS = {
  scheduled: "#f59e0b",
  delivered: "#22c55e",
  failed: "#ef4444",
}

const MessageItem = styled.div`
  background: var(--surface-1);
  border-radius: 10px;
  padding: 8px 13px;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 56px;
`

const MessageIcon = styled.span<{ $status: MessageStatus }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-sizing: border-box;
  /* the clock svg draws its circle inset in a 20px box (~16.5px visible), so bordered circles match it */
  width: ${({ $status }) => ($status === "scheduled" ? "20px" : "16.5px")};
  height: ${({ $status }) => ($status === "scheduled" ? "20px" : "16.5px")};
  margin: ${({ $status }) => ($status === "scheduled" ? "1px 0 0" : "2.75px 1.75px 0")};
  border-radius: 50%;
  border: ${({ $status }) => ($status === "scheduled" ? "none" : `1.5px solid ${STATUS_COLORS[$status]}`)};

  svg {
    width: ${({ $status }) => ($status === "scheduled" ? "20px" : "9px")};
    height: ${({ $status }) => ($status === "scheduled" ? "20px" : "9px")};

    path {
      stroke: ${({ $status }) => STATUS_COLORS[$status]};
    }
  }
`

const MessageTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 4px;
`

const MessageDate = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: var(--text-muted);

  b {
    font-weight: 600;
    color: var(--text-secondary);
  }
`

type MessageStatus = "scheduled" | "delivered" | "failed"

const STATUS_LABELS: Record<MessageStatus, string> = {
  scheduled: "Scheduled",
  delivered: "Delivered",
  failed: "Failed",
}

const STATUS_ICONS: Record<MessageStatus, JSX.Element> = {
  scheduled: <IconClock />,
  delivered: <IconCheck />,
  failed: <IconCross />,
}

interface WidgetMessage {
  key: string
  title: string
  date: string | null | undefined
  status: MessageStatus
}

export const MessagesWidget = () => {
  const { devices, currentDevice } = useCustomerDashboard()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const userProfile = useAppSelector(state => state.user.userProfile)
  const hasMessagesAccess = userProfile
    ? hasSubscriptionAccess(userProfile, SubscriptionPrivilege.MessageCenter)
    : false
  const selectedDevice = devices[currentDevice]

  const [triggerGetDevicesMessages, { data: scheduledData, isFetching: isFetchingScheduled }] =
    useLazyGetDevicesMessagesQuery()
  const [triggerGetTemplates, { data: templatesData }] = useLazyGetMessageTemplatesQuery()
  const [triggerGetDelivered, { data: deliveredData, isFetching: isFetchingDelivered }] =
    useLazyGetDeviceMessageHistoryQuery()
  const [triggerGetFailed, { data: failedData, isFetching: isFetchingFailed }] = useLazyGetDeviceMessageHistoryQuery()

  useEffect(() => {
    if (!selectedDevice?.id || !hasMessagesAccess) return

    const historyParams = { orderColumn: "delivered_date", orderType: "DESC" as const, perPage: MAX_ITEMS, page: 1 }
    triggerGetDevicesMessages([selectedDevice.id])
    triggerGetTemplates()
    triggerGetDelivered({
      deviceId: selectedDevice.id,
      params: { ...historyParams, delivery_status: MessageDeliveryStatus.DELIVERED },
    })
    triggerGetFailed({
      deviceId: selectedDevice.id,
      params: { ...historyParams, delivery_status: MessageDeliveryStatus.FAILED },
    })
  }, [
    selectedDevice?.id,
    hasMessagesAccess,
    triggerGetDevicesMessages,
    triggerGetTemplates,
    triggerGetDelivered,
    triggerGetFailed,
  ])

  const widgetMessages = useMemo<WidgetMessage[]>(() => {
    const templateTitleById = new Map<number, string>()
    templatesData?.data?.forEach(template => templateTitleById.set(template.id, template.title))

    const scheduled = (
      scheduledData?.data?.find(item => item.device_id === selectedDevice?.id)?.messages ?? []
    ).map<WidgetMessage>(message => ({
      key: `scheduled-${message.message_id}`,
      title: templateTitleById.get(message.message_template_id) ?? "",
      date: message.scheduled_at,
      status: "scheduled",
    }))

    const deliveredOrFailed = [...(deliveredData?.data ?? []), ...(failedData?.data ?? [])]
      .sort((a, b) => (b.last_delivery_at ?? "").localeCompare(a.last_delivery_at ?? ""))
      .map<WidgetMessage>(message => ({
        key: `history-${message.id}`,
        title: message.title,
        date: message.last_delivery_at,
        status: message.delivery_status as MessageStatus,
      }))

    return [...scheduled.slice(0, MAX_ITEMS), ...deliveredOrFailed].slice(0, MAX_ITEMS)
  }, [scheduledData, templatesData, deliveredData, failedData, selectedDevice?.id])

  const isFetching = isFetchingScheduled || isFetchingDelivered || isFetchingFailed

  if (!selectedDevice) {
    return null
  }

  return (
    <Widget>
      <WidgetTitle>Messages</WidgetTitle>
      <RestrictedAccessWidget privilege={SubscriptionPrivilege.MessageCenter}>
        <WidgetContent>
          {isFetching ? (
            <LoadingContainer>
              <Spinner />
            </LoadingContainer>
          ) : widgetMessages.length === 0 ? (
            <NoAlertsText>No messages for this device</NoAlertsText>
          ) : (
            <>
              {widgetMessages.map(message => (
                <MessageItem key={message.key}>
                  <MessageIcon $status={message.status}>{STATUS_ICONS[message.status]}</MessageIcon>
                  <div>
                    <MessageTitle>{message.title}</MessageTitle>
                    <MessageDate>
                      <b>{STATUS_LABELS[message.status]}:</b> {parseDateField(message.date, DATE_FORMAT)}
                    </MessageDate>
                  </div>
                </MessageItem>
              ))}
            </>
          )}
        </WidgetContent>
        <WidgetButton>
          <StyledWidgetButton onClick={() => navigate(`/customers/${id}/messages`)} size="auto" color="blueBordered">
            View All
          </StyledWidgetButton>
        </WidgetButton>
      </RestrictedAccessWidget>
    </Widget>
  )
}
